import type {
  SelectableCountry,
  SignInOTPRequest,
  SignUpRequest,
} from '@/types';

import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { GoToSignUpButton } from '@/app/auth/sign-in/_components';
import { Box, Button, MobileInput, Stack } from '@/components';
import { useInputAttributes, useSignInOtp, useTranslation } from '@/hooks';
import { englishOnlyParams, mobileRegex, yupValidator } from '@/utils';

type CodeLoginMobileFormData = Pick<SignInOTPRequest, 'mobile'>;

type CodeLoginMobileFormProps = {
  goNext: (data: SignInOTPRequest) => void;
};

const CodeLoginMobileForm: React.FC<CodeLoginMobileFormProps> = ({
  goNext,
}) => {
  const { t } = useTranslation('pages.signIn.form.codeLoginForm.mobileForm');

  const [selectedCountry, setSelectedCountry] =
    React.useState<SelectableCountry>('iran');

  const countryCodes: Record<
    SelectableCountry,
    SignUpRequest['country_phone_prefix']
  > = {
    iran: '+98',
    iraq: '+964',
    afghanistan: '+93',
  };

  const validationSchema = yupValidator.object().shape({
    mobile: yupValidator
      .string()
      .test({ ...englishOnlyParams })
      .matches(mobileRegex[selectedCountry], t('invalidNumber'))
      .label(t('mobile'))
      .required(),
  });

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const filedInputProps = useInputAttributes<CodeLoginMobileFormData>(
    control,
    register,
    errors,
    {
      mobile: t('mobile'),
    }
  );

  const { mutateAsync: getOtp, isPending } = useSignInOtp();

  const onSubmit = (data: CodeLoginMobileFormData) => {
    const payload = {
      ...data,
      country_code_prefix: countryCodes[selectedCountry],
    };
    void getOtp(payload).then(() => {
      goNext(payload);
    });
  };

  return (
    <Stack as="form" w="100%" pt={4} flex={1}>
      <Box gap={4} w="full" display="flex" flexDirection="column">
        <MobileInput
          inputProps={{ ...filedInputProps.mobile }}
          selectedCountry={selectedCountry}
          setSelectedCountry={setSelectedCountry}
        />
      </Box>
      <Stack w="full" align="center" gap={6} justify="end" py={4} flexGrow={1}>
        <Button
          w="full"
          type="submit"
          onClick={(data) => void handleSubmit(onSubmit)(data)}
          isLoading={isPending}
        >
          {t('sendCode')}
        </Button>
        <GoToSignUpButton />
      </Stack>
    </Stack>
  );
};

export default CodeLoginMobileForm;
